/* eslint-disable react-hooks/exhaustive-deps */
import { AppShell, LoadingOverlay } from "@mantine/core";
import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useAuthInfo } from "@propelauth/react";
import { useDisclosure } from "@mantine/hooks";
import { formatISO, fromUnixTime } from "date-fns";
// SpotlightSearch removed
import {
  useTraitsAndMetrics,
  useViews,
  useWorkspaceMembers,
  useWorkspacePreferences,
} from "../../hooks/QueryHooks";
import useSelectWorkspace from "../../hooks/useSelectWorkspace";
import LeftSideBar from "./components/LeftSideBar";
import usePreferencesStore from "../../store/preference/preferencesStore";
import { WorkspacePreferenceResponse } from "../../services/preference/preferenceEndpoints.interface";

import DynamicHeader from "../../components/layout/DynamicHeader";
import useHeaderStore from "../../store/layout/headerStore";
import GlobalSpotlight from "../../shared/spotlight/GlobalSpotlight";

const Homepage = () => {
  const [menu, setMenu] = useState<boolean>(true);
  const { showHeader } = useHeaderStore();
  const [opened, { toggle }] = useDisclosure();
  const authInfo: any = useAuthInfo();
  const { data, isLoading } = useSelectWorkspace();

  useWorkspaceMembers({
    workspaceId: data?.id,
    enabled: !!data?.id,
    retry: 2,
  });

  useTraitsAndMetrics({
    workspaceId: data?.id,
    enabled: !!data?.id,
  });
  
  // Fetch workspace preferences
  const preferences = useWorkspacePreferences({
    workspaceId: data?.id,
    enabled: !!data?.id,
  });

  // Store preferences in Zustand
  const setPreferences = usePreferencesStore(state => state.setPreferences);
  const setWorkspaceId = usePreferencesStore(state => state.setWorkspaceId);

  const views: any = useViews({
    workspaceId: data?.id,
    enabled: !!data?.id,
  });

  const handleMenu = (change: any) => {
    setMenu(change);
  };

  useEffect(() => {
    document.title = "Home | Hyperengage";
  }, []);
  
  // Store workspace preferences in Zustand when available
  useEffect(() => {
    if (data?.id) {
      setWorkspaceId(data.id);
    }
    
    // Check if we have valid preferences data and store it
    const preferencesData = preferences.data as WorkspacePreferenceResponse;

    if (preferencesData && 
        preferencesData.accounts && 
        preferencesData.users && 
        preferencesData.id) {
      setPreferences(preferencesData);
    }
  }, [data?.id, preferences.data, setPreferences, setWorkspaceId]);

  useEffect(() => {
    const selectedWorkspace: any =
      authInfo.orgHelper?.getOrg(data?.id) || undefined;
    if (
      process.env.REACT_APP_HE_WORKSPACE_KEY &&
      process.env.REACT_APP_HE_DATA_KEY &&
      selectedWorkspace?.orgId
    ) {
      window?.hyperengage("identify_account", {
        account_id: selectedWorkspace?.orgId,
        traits: {
          name: selectedWorkspace?.orgName,
          unique_identifier: selectedWorkspace?.orgMetadata?.unique_identifier,
          setup_complete: selectedWorkspace?.orgMetadata?.setup_complete,
          tracking_website_domain: data?.tracking_website_domain,
          workspace_default_currency: data?.workspace_default_currency,
          workspace_time_zone: data?.workspace_time_zone,
          avatar_url: data?.workspace_logo,
          mrr: 0,
          is_trial: true,
          created_at: data?.created_at,
        },
      });
      if (authInfo.user) {
        window?.hyperengage("identify_user", {
          user_id: authInfo.user.userId,
          traits: {
            created_at: formatISO(fromUnixTime(authInfo.user.createdAt)),
            name: `${authInfo.user.firstName} ${authInfo.user.lastName}`,
            first_name: authInfo.user.firstName,
            last_name: authInfo.user.lastName,
            email: authInfo.user.email,
            avatar_url: authInfo.user.pictureUrl,
            properties: authInfo.user?.properties,
            can_create_orgs: authInfo.user?.can_create_orgs,
            mfa_enabled: authInfo.user?.mfa_enabled,
          },
        });
      }
    }
  }, [data?.id]);

  return (
    <div className="bg-gray-50">
      <GlobalSpotlight />
    <AppShell
      layout="alt"
      p="0 10px 0 0px"
      withBorder={false}
      header={{ height: 60 }}
      navbar={{ width: menu ? 230 : 75, breakpoint: 'sm', collapsed: { mobile: !opened } }}
    >
      <LoadingOverlay
        loaderProps={{ size: "lg" }}
        color="violet"
        visible={views.isLoading || isLoading || preferences.isLoading}
      />
      
      {data?.id && (
        <>
          {showHeader && <AppShell.Header className="bg-gray-50">
            <DynamicHeader opened={opened} toggle={toggle} />
          </AppShell.Header>}

          <AppShell.Navbar className="bg-gray-50" style={{ height: 'calc(100vh - 25px)' }}>
            <LeftSideBar
              menu={menu}
              workspace={data}
              setMenu={handleMenu}
              views={views?.data || [{ text: "empty", to: "/" }]}
            />
          </AppShell.Navbar>
          <AppShell.Main className="ease-linear duration-100 bg-gray-50 pb-3">
            <div className="rounded-lg bg-white border-2 h-min border-gray-100">
            {views?.data?.length && (
              <Outlet />
            )}
            </div>
          </AppShell.Main>
        </>
      )}
    </AppShell>
    </div>
  );
};

export default Homepage;
